"use client"

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { useCities } from '@/hooks/useCities'
import { CountryFlag } from '@/lib/countryFlags'
import type { AccordionRegion } from '@/lib/locationRegions'
import {
  buildAccordionRegions,
  countrySentinelForIso,
  getFlagCountryForLocationSelection,
  getLocationFilterDisplay,
  getMultiLocationFilterDisplay,
  isCountrySentinel,
  resolveSentinelToCities,
} from '@/lib/locationRegions'
import { LocationRegionsFlatList } from '@/components/shared/LocationRegionsFlatList'

function selectedCityToDraftCities(selectedCity: string, launchedCities: string[]): string[] {
  if (!selectedCity) return []
  if (isCountrySentinel(selectedCity)) {
    const expanded = resolveSentinelToCities(selectedCity)
    if (!expanded?.length) return []
    const launched = new Set(launchedCities)
    return expanded.filter((c) => launched.has(c)).sort((a, b) => a.localeCompare(b))
  }
  return launchedCities.includes(selectedCity) ? [selectedCity] : []
}

/** Collapse multi city draft to one matcher `selectedCity` (city string or `country:ISO2`). */
function draftToMatcherStoredValue(
  draft: string[],
  launchedCities: string[],
  accordionRegions: AccordionRegion[],
): string | null {
  const launched = new Set(launchedCities)
  const sorted = [...new Set(draft)].filter((c) => launched.has(c)).sort((a, b) => a.localeCompare(b))
  if (sorted.length === 0) return null

  for (const region of accordionRegions) {
    if (!region.iso2) continue
    const regionLaunched = region.cities.filter((c) => launched.has(c))
    if (regionLaunched.length === 0 || regionLaunched.length === 1) continue
    if (
      regionLaunched.length === sorted.length &&
      regionLaunched.every((c) => sorted.includes(c))
    ) {
      return countrySentinelForIso(region.iso2)
    }
  }

  if (sorted.length === 1) return sorted[0]!
  return sorted[0]!
}

export interface MatcherFeedLocationPickerProps {
  selectedCity: string
  onSaved: (city: string) => void
  disabled?: boolean
}

/**
 * Bottom sheet copy of the feed {@link LocationFilter}: same region/city checklist, single “Save” only.
 * Maps the multi draft to one `selectedCity` for the pairwise API.
 */
export function MatcherFeedLocationPicker({
  selectedCity,
  onSaved,
  disabled = false,
}: MatcherFeedLocationPickerProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = React.useState(false)
  const [draftCities, setDraftCities] = React.useState<string[]>([])
  const { cities, cityCountryByCity, loading: citiesLoading, error: citiesError } = useCities()

  const allLocationsText = t('onskin.filters.allLocations')

  const accordionRegions = React.useMemo(
    () => buildAccordionRegions(cities, cityCountryByCity ?? {}),
    [cities, cityCountryByCity],
  )

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (disabled) return
      if (open && cities.length > 0) {
        setDraftCities(selectedCityToDraftCities(selectedCity, cities))
      } else if (open) {
        setDraftCities([])
      }
      setIsOpen(open)
    },
    [disabled, selectedCity, cities],
  )

  const selectedLocationsForDisplay = React.useMemo(() => {
    if (!selectedCity) return []
    if (isCountrySentinel(selectedCity)) {
      const list = resolveSentinelToCities(selectedCity)?.filter((c) => cities.includes(c)) ?? []
      return list.length ? list : [selectedCity]
    }
    return [selectedCity]
  }, [selectedCity, cities])

  const displayText = React.useMemo(() => {
    if (!selectedCity) {
      return t('matcher.tapToChooseLocations', 'Tap to choose locations')
    }
    if (isCountrySentinel(selectedCity)) {
      const launched = new Set(cities)
      const list =
        resolveSentinelToCities(selectedCity)?.filter((c) => launched.has(c)) ?? []
      if (list.length === 1) {
        return list[0]!
      }
      const countryOnly = getLocationFilterDisplay(selectedCity, allLocationsText, launched)
      if (list.length > 1) {
        return `${countryOnly} — ${t('matcher.allCities', 'all cities')}`
      }
      return countryOnly
    }
    if (selectedLocationsForDisplay.length <= 1) {
      return selectedCity
    }
    return getMultiLocationFilterDisplay(
      selectedLocationsForDisplay,
      allLocationsText,
      accordionRegions,
    )
  }, [selectedCity, selectedLocationsForDisplay, allLocationsText, accordionRegions, t])

  const flagCountry = selectedCity
    ? getFlagCountryForLocationSelection(selectedCity, cityCountryByCity ?? {})
    : undefined

  const draftCountryNames = React.useMemo(() => {
    const map = cityCountryByCity ?? {}
    return new Set(draftCities.map((c) => map[c]?.trim()).filter(Boolean))
  }, [draftCities, cityCountryByCity])

  const multiCountryDraft = draftCountryNames.size > 1

  const storedPreview = React.useMemo(
    () => (draftCities.length ? draftToMatcherStoredValue(draftCities, cities, accordionRegions) : null),
    [draftCities, cities, accordionRegions],
  )

  const saveDisabled =
    disabled || draftCities.length === 0 || multiCountryDraft || !storedPreview

  const handleSave = () => {
    if (saveDisabled || !storedPreview) return
    onSaved(storedPreview)
    setIsOpen(false)
  }

  const isLoading = citiesLoading

  return (
    <>
      <button
        type="button"
        id="matcher-location-picker"
        className={cn(
          'flex h-10 w-full min-w-0 items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-left text-base sm:text-sm transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/40',
        )}
        style={{ fontSize: '16px' }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => handleOpenChange(true)}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 truncate text-foreground">{displayText}</span>
          {flagCountry ? (
            <CountryFlag
              country={flagCountry}
              size="sm"
              showBorder={false}
              className="shrink-0 !rounded-sm"
            />
          ) : null}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
      </button>

      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="flex h-auto max-h-[min(560px,85vh)] flex-col p-6"
          hideCloseButton
        >
          <SheetHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <SheetTitle className="flex items-center gap-2 text-xl font-semibold">
              <MapPin className="h-5 w-5 shrink-0" aria-hidden />
              {t('filters.location')}
            </SheetTitle>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">
                  {t('filters.loadingLocations')}
                </div>
              </div>
            ) : citiesError ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <div className="text-sm text-destructive">{t('filters.failedToLoad')}</div>
                <div className="max-w-xs break-words text-center text-xs text-muted-foreground">
                  {String(citiesError instanceof Error ? citiesError.message : citiesError)}
                </div>
              </div>
            ) : (
              <LocationRegionsFlatList
                multiSelect
                regions={accordionRegions}
                selectedCities={draftCities}
                allLocationsText={allLocationsText}
                onSelectionChange={setDraftCities}
              />
            )}
          </div>

          {multiCountryDraft ? (
            <p className="pt-2 text-center text-xs text-destructive">
              {t(
                'matcher.oneCountryOnly',
                'Pick cities in one country at a time for matcher results.',
              )}
            </p>
          ) : null}

          <div className="shrink-0 pt-4">
            <Button className="h-12 w-full rounded-[1.75rem] md:rounded-2xl" disabled={saveDisabled} onClick={handleSave}>
              {t('common.save', 'Save')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
